import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Star, CheckCircle2, ArrowLeft, Heart } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const RATING_LABELS = { 1: "Da migliorare", 2: "Buono", 3: "Ottimo" };

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function EventoFeedback() {
  const { token } = useParams();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [commento, setCommento] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/api/eventi/feedback/${token}`)
      .then((r) => {
        setContext(r.data);
        if (r.data.already_submitted) setSubmitted(true);
      })
      .catch((e) => setError(e.response?.data?.detail || "Link non valido"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (!rating) {
      toast({ description: "Seleziona quante stelline per iniziare.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/eventi/feedback/${token}`, { rating, commento });
      setSubmitted(true);
      toast({ description: "Grazie per il tuo feedback!" });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast({ description: `Errore: ${detail}`, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-24 text-center text-slate-500">
          Caricamento…
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !context) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Feedback — A.C.D.T." />
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-24 text-center">
          <h1 className="text-3xl font-extrabold text-[#0a1f55]">Link non valido</h1>
          <p className="mt-3 text-slate-600">Il link di feedback che hai usato non è valido o è scaduto.</p>
          <Link to="/eventi">
            <Button data-testid="feedback-back-eventi" className="mt-6 bg-[#0a1f55] hover:bg-[#102a6b]">
              <ArrowLeft className="mr-2 h-4 w-4" /> Torna agli eventi
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ev = context.evento;

  return (
    <div className="relative min-h-screen bg-white" data-testid="feedback-page">
      <SEO title={`Feedback — ${ev.titolo}`} description="Lascia il tuo feedback sull'evento A.C.D.T." />
      <Navbar />

      <section className="relative pt-16 pb-8 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 relative text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/20 text-[#fbbf24] text-[11px] font-extrabold uppercase tracking-[0.2em] mb-4">
            Sondaggio · 30 secondi
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold leading-tight">Grazie per aver partecipato!</h1>
          <p className="mt-3 text-slate-200">
            <span className="font-bold">{ev.titolo}</span>
            {ev.data_inizio && <> · {formatDate(ev.data_inizio)}</>}
          </p>
        </div>
      </section>

      <section className="relative py-12">
        <div className="max-w-2xl mx-auto px-5">
          {submitted ? (
            <div
              className="rounded-3xl border border-emerald-200 bg-emerald-50 p-10 text-center"
              data-testid="feedback-submitted"
            >
              <div className="inline-flex h-16 w-16 rounded-full bg-emerald-500 text-white items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-extrabold text-emerald-800">Feedback inviato!</h2>
              <p className="mt-3 text-emerald-700 text-[15px] leading-relaxed">
                Grazie per il tuo tempo. Le tue risposte ci aiutano a rendere i prossimi eventi ancora più utili.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link to="/eventi">
                  <Button data-testid="feedback-explore-eventi" className="bg-[#0a1f55] hover:bg-[#102a6b]">
                    Scopri altri eventi
                  </Button>
                </Link>
                <Link to="/corso">
                  <Button variant="secondary" data-testid="feedback-explore-corso">
                    Vai al Corso A.C.D.T.
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white shadow-lg p-8 md:p-10">
              <p className="text-slate-600 text-[15px]">
                Ciao <span className="font-bold text-[#0a1f55]">{context.iscrizione.nome}</span>,
                come è andato l&apos;evento?
              </p>

              <div className="mt-6">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                  Il tuo voto
                </div>
                <div className="flex items-center justify-center gap-3" data-testid="feedback-stars">
                  {[1, 2, 3].map((n) => {
                    const active = (hover || rating) >= n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        data-testid={`feedback-star-${n}`}
                        aria-label={`${n} stelline`}
                        className="p-2 rounded-full hover:bg-yellow-50 transition-all focus:outline-none"
                      >
                        <Star
                          className={`h-12 w-12 transition-all ${
                            active
                              ? "fill-[#f59e0b] text-[#f59e0b] scale-110"
                              : "text-slate-300"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
                <div className="text-center mt-2 h-5 text-sm font-bold text-[#0a1f55]">
                  {rating ? RATING_LABELS[rating] : hover ? RATING_LABELS[hover] : ""}
                </div>
              </div>

              <div className="mt-8">
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                  Un commento (opzionale)
                </label>
                <Textarea
                  rows={4}
                  value={commento}
                  onChange={(e) => setCommento(e.target.value)}
                  placeholder="Cosa ti è piaciuto? Cosa possiamo migliorare?"
                  maxLength={1000}
                  data-testid="feedback-commento"
                />
                <div className="text-right text-[11px] text-slate-400 mt-1">
                  {commento.length}/1000
                </div>
              </div>

              <div className="mt-8">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !rating}
                  data-testid="feedback-submit"
                  className="w-full bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold py-6 text-base"
                >
                  {submitting ? "Invio in corso…" : "Invia feedback"}
                  <Heart className="ml-2 h-4 w-4" />
                </Button>
                <p className="mt-3 text-center text-[11px] text-slate-400">
                  Il tuo feedback è visibile solo agli organizzatori A.C.D.T.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
